import { useState, useEffect, useRef } from 'react';
import { analysisApi } from '../api/analysisApi';

/**
 * Custom hook to monitor SMART analysis progress
 */
export const useSmartAnalysis = (filename) => {
  const [smartProgress, setSmartProgress] = useState({});
  const [smartResults, setSmartResults] = useState(null);
  const [currentSmartFile, setCurrentSmartFile] = useState(null);
  const [showSmartDialog, setShowSmartDialog] = useState(false);
  const pollingIntervalRef = useRef(null);

  const fetchSmartResults = async (targetFilename) => {
    if (!targetFilename) return;

    try {
      const data = await analysisApi.getSmartResults(targetFilename);
      setSmartResults(data);
      setShowSmartDialog(true);
      setCurrentSmartFile(targetFilename);
    } catch (error) {
      console.error('Error fetching SMART results:', error);
      throw error;
    }
  };

  const triggerSmartAnalysis = async (targetFilename, validatedOnly = false) => {
    if (!targetFilename) return;

    try {
      const response = await analysisApi.triggerSmartAnalysis(targetFilename, validatedOnly);
      
      console.log('[triggerSmartAnalysis] Success response:', response);
      
      setCurrentSmartFile(targetFilename);
      setSmartProgress(prev => ({ ...prev, [targetFilename]: { status: 'processing' } }));
      startSmartPolling(targetFilename);
      
      return response;
    } catch (error) {
      console.error('[triggerSmartAnalysis] Error triggering SMART analysis:', error);
      console.error('[triggerSmartAnalysis] Error response:', error.response);
      console.error('[triggerSmartAnalysis] Error data:', error.response?.data);
      console.error('[triggerSmartAnalysis] Error status:', error.response?.status);
      
      // Extract meaningful error message
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.message 
        || error.response?.data?.details
        || error.message 
        || 'Unknown error occurred';
      
      throw new Error(errorMessage);
    }
  };

  const startSmartPolling = (targetFilename) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const data = await analysisApi.getSmartResults(targetFilename);
        if (data && data.status === 'completed') {
          setSmartProgress(prev => ({ ...prev, [targetFilename]: data }));
          setSmartResults(data);
          setShowSmartDialog(true);
          stopSmartPolling();
        }
      } catch (error) {
        console.error('Error polling SMART results:', error);
      }
    }, 3000);
  };

  const stopSmartPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => stopSmartPolling();
  }, []);

  return {
    smartProgress,
    smartResults,
    currentSmartFile,
    showSmartDialog,
    setShowSmartDialog,
    handleViewSmartResults: fetchSmartResults,
    handleStartSmartAnalysis: triggerSmartAnalysis,
    setSmartResults
  };
};

export default useSmartAnalysis;
